import { ATRIUM_HALF } from "@mall-mart/shared";

export type WingRegion = "north" | "east" | "west" | null;

/** Which wing corridor (either floor) the position is in; null in the
 *  atrium/food court. Wing corridors start past the atrium walls. */
export function wingAt(x: number, z: number): WingRegion {
  if (z > ATRIUM_HALF) {
    return "north";
  }
  if (x > ATRIUM_HALF) {
    return "east";
  }
  if (x < -ATRIUM_HALF) {
    return "west";
  }
  return null;
}

/**
 * Fires one product prefetch per store the first time the player enters a
 * wing, so interiors stream in from warm caches instead of cold round trips.
 * Prefetches run sequentially (not 16 at once) and once per wing per
 * session; the shopsApi client cache handles TTL/refresh from there.
 */
export class WingPrefetcher {
  private readonly wingShopIds: Record<Exclude<WingRegion, null>, string[]>;
  private readonly prefetch: (shopId: string) => Promise<unknown>;
  private readonly started: Set<string> = new Set();

  constructor(
    wingShopIds: Record<Exclude<WingRegion, null>, string[]>,
    prefetch: (shopId: string) => Promise<unknown>
  ) {
    this.wingShopIds = wingShopIds;
    this.prefetch = prefetch;
  }

  update(x: number, z: number): void {
    const wing: WingRegion = wingAt(x, z);
    if (wing === null || this.started.has(wing)) {
      return;
    }
    this.started.add(wing);
    const ids: string[] = this.wingShopIds[wing];
    void (async () => {
      for (const id of ids) {
        try {
          await this.prefetch(id);
        } catch {
          // A failed prefetch is harmless; the normal on-approach fetch retries.
        }
      }
    })();
  }
}
